import React, { useEffect, useRef, useState } from "react"
import ReactCountryFlag from "react-country-flag"
import * as styles from "./language-selector.module.scss"
import * as config from "../../../../../../config"
import { arrowBackIcon } from "../../../../../../static/images"

import brFlag from "../../../../../../static/images/brFlag.png"
import luaFlag from "../../../../../../static/images/luaFlag.png"
import { detectWebView } from "../../../../../utils/detectWebView"

export type RelativeLanguagePaths = {
  locale: string
  value: string
}[]

type RenderProps = {
  locale: string
  relativeLanguagePaths?: RelativeLanguagePaths
  openUpward?: boolean
}

const LanguageSelector: React.FC<RenderProps> = ({ locale, relativeLanguagePaths, openUpward = false }) => {
  const [isOpen, setIsOpen] = useState(false)
  const menuButtonRef = useRef(null)
  const menuContainerRef = useRef(null)

  const mainLocale = config.locales[0].toLowerCase().split("-").reverse().join("-")
  const otherLocales = config.locales
    .map(otherLocale => otherLocale.toLowerCase().split("-").reverse().join("-"))
    .filter(locales => locales !== locale)

  const focusMenu = () => menuButtonRef?.current?.focus()
  const focusFirstMenuItem = () => {
    if (typeof window === "undefined") return
    const firstMenuItem = document.getElementsByClassName("jsMenuItem")[0] as HTMLElement
    if (!firstMenuItem) {
      return
    }
    firstMenuItem.focus()
  }
  const openMenu = () => setIsOpen(true)
  const closeMenu = () => setIsOpen(false)
  const hasDropdown = otherLocales.length > 0

  // When the menu lose focus, we need to close it
  useEffect(() => {
    if (!isOpen || typeof window === "undefined") {
      return
    }
    const onFocus = event => {
      if (!event.target.classList || !event.target.classList.contains("jsMenuFocusable")) {
        closeMenu()
        window.removeEventListener("focus", onFocus, true)
      }
    }
    window.addEventListener("focus", onFocus, true)
    return () => window.removeEventListener("focus", onFocus, true)
  }, [isOpen])

  const closeMenuOnEscape = <T extends React.KeyboardEvent>(event: T) => {
    if (event.key === "Escape") {
      event.preventDefault()
      focusMenu()
      closeMenu()
    }
  }

  const getLocaleLink = (currentLocale: string) => {
    const relativeLocaleLink = relativeLanguagePaths?.find(relativeLink => relativeLink.locale === currentLocale)
    if (currentLocale === mainLocale) {
      return `/${relativeLocaleLink ? relativeLocaleLink.value : ""}`
    }
    return `/${currentLocale}/${relativeLocaleLink ? relativeLocaleLink.value : ""}`
  }

  const trigram = process.env.GATSBY_SUBSIDIARY_TRIGRAM
  const WEBVIEW_FLAG_WIDTH = "229px"
  const WEBVIEW_FLAG_HEIGHT = "51px"

  const getLocaleFlag = (localeFlag: string) => {
    switch (localeFlag) {
      case "en":
      case "gb-en":
        return "gb"
      case "us-en":
        return "us"
      case "eu-en":
        return "eu"
      case "sn-fr":
        return "sn"
      default:
        return localeFlag
    }
  }
  const renderBrFlag = () => {
    // Manually rendering the brittany flag to bypass the react-country-flag that doesn't support the brittany flag
    // Only the MOR (Morlaix) subsidiary uses the brittany flag
    return (
      <img
        src={brFlag}
        alt=""
        className={styles.countryFlag}
        style={{
          display: "inline-block",
          verticalAlign: "middle",
          width: isWebView ? WEBVIEW_FLAG_WIDTH : "29px",
          height: isWebView ? WEBVIEW_FLAG_HEIGHT : "17px",
        }}
      />
    )
  }

  const renderLuaFlag = () => {
    // Manually rendering the brittany flag to bypass the react-country-flag that doesn't support the brittany flag
    // Only the LUAS SUB subsidiary uses the brittany flag
    return (
      <img
        src={luaFlag}
        alt=""
        className={styles.countryFlag}
        style={{
          display: "inline-block",
          verticalAlign: "middle",
          width: "29px",
          height: "17px",
        }}
      />
    )
  }

  const renderCountryFlag = (localeFlag: string) => {
    if (trigram === "MOR" && getLocaleFlag(localeFlag) === "br") {
      return renderBrFlag()
    }

    //Or set directly trigram === LUA ???
    if ((trigram === "LUA" && localeFlag === "en") || (trigram === "LUA" && localeFlag === "ga")) {
      return renderLuaFlag()
    }

    return (
      <ReactCountryFlag
        className={styles.countryFlag}
        countryCode={getLocaleFlag(localeFlag)}
        style={{
          width: isWebView ? WEBVIEW_FLAG_WIDTH : "29px",
          height: isWebView ? WEBVIEW_FLAG_HEIGHT : "21px",
        }}
        alt=""
        svg
      />
    )
  }

  const languageSelectorLabel = config["language_selector_title"][config.locale]
  const menuAriaLabel = [locale, languageSelectorLabel].filter(Boolean).join(" - ")

  const isWebView = detectWebView()

  if (!hasDropdown) {
    // Single language with no switch possible: hide the flag entirely in webview only
    if (isWebView) {
      return null
    }
    return (
      <div className={styles.languageSelectorMenu}>
        <button
          type="button"
          id="languageSelectorMenu"
          className={styles.menuButton}
          disabled
          aria-label={menuAriaLabel}
        >
          {renderCountryFlag(locale)}
          <span className={styles.countryLabel} lang={locale.split("-")[0]}>
            {locale}
          </span>
        </button>
      </div>
    )
  }

  return (
    <div className={`${styles.languageSelectorMenu} ${openUpward ? styles.openUpward : ""}`}>
      <button
        id="languageSelectorMenu"
        className={styles.menuButton + " jsMenuFocusable"}
        aria-haspopup={hasDropdown}
        aria-controls="flagItems"
        aria-expanded={isOpen && hasDropdown}
        onClick={() => {
          if (isOpen) {
            closeMenu()
            return
          }
          openMenu()
          focusFirstMenuItem()
        }}
        aria-label={menuAriaLabel}
        onKeyDown={closeMenuOnEscape}
        ref={menuButtonRef}
        disabled={!hasDropdown}
      >
        {renderCountryFlag(locale)}
        {!isWebView && (
          <span className={styles.countryLabel} lang={locale.split("-")[0]}>
            {locale}
          </span>
        )}
        {!isWebView && <img src={arrowBackIcon} className={styles.arrowIcon} alt="" aria-hidden="true" />}
      </button>
      <div className={isOpen ? `${styles.dropdownContent} ${styles.active}` : styles.dropdownContent}>
        <ul id="flagItems" aria-labelledby="languageSelectorMenu" ref={menuContainerRef}>
          {otherLocales.map((otherLocale, index) => {
            return (
              <li key={index}>
                <a
                  className={styles.subMenuButton + " jsMenuFocusable jsMenuItem"}
                  href={getLocaleLink(otherLocale)}
                  onKeyDown={closeMenuOnEscape}
                >
                  {renderCountryFlag(otherLocale)}
                  {!isWebView && (
                    <span className={styles.countryLabel} lang={otherLocale.split("-")[0]}>
                      {otherLocale}
                    </span>
                  )}
                </a>
              </li>
            )
          })}
        </ul>
      </div>
    </div>
  )
}

export default LanguageSelector
